import React, { Fragment, useState, useEffect } from 'react';
import styles from './styles.scss';
import classnames from 'classnames';

import {
  Button,
  Icons,
  InputBox,
  AppBar,
  AppBarTitle,
  AppBarSection,
  AppBarContext,
} from '@densityco/ui';
import Modal from '../../Modal';
import Tooltip from '../../Tooltip';
import HorizontalForm from '../../HorizontalForm';

import MoreButton from '../../MoreButton';

import colorVariables from '@densityco/ui/variables/colors.json';

function isNumeric(text) {
  return /^[\d.]+$/.test(text);
}

export const SensorRearranger: React.FunctionComponent<{
  positions: Array<{
    serialNumber: string;
    spacingRight: string;
    doorwayName: string;
  }>;
  onSetSpacingRight: (serialNumber: string, spacingRight: string) => void;
  onAddSensor: () => void;
  onDeleteSensor: (serialNumber: string) => void;
  onReorderSensors: (serialNumbers: Array<string>) => void;
}> = ({
  positions,
  onSetSpacingRight,
  onAddSensor,
  onDeleteSensor,
  onReorderSensors,
}) => {
  const [focusedUnits, setFocusedUnits] = useState([null, null]);
  const [showSidebar, setShowSidebar] = useState(false);

  // State for the reordering modal
  const [showSensorArrangement, setShowSensorArrangement] = useState(false);
  const [serialNumberPositions, setSerialNumberPositions] = useState(
    positions.map(s => s.serialNumber),
  );
  // When the sensors change, update the serial number positions with the updated data.
  useEffect(() => {
    setSerialNumberPositions(positions.map(s => s.serialNumber));
  }, [positions]);

  const areUnitsSelected = !(
    focusedUnits[0] === null && focusedUnits[1] === null
  );

  // This component can render in two ways:
  // - All the sensors horizontally centered in the container
  // - All the sensors left justified horizontally in the container, with horizontal scrolling.
  //
  // This condition changes which of those layouts should be used.
  const renderWithCenteredLayout = positions.length <= 3;

  return (
    <Fragment>
      {/* SENSOR ARRANGEMENT MODAL */}
      <Modal
        visible={showSensorArrangement}
        onBlur={() => setShowSensorArrangement(false)}
        onEscape={() => setShowSensorArrangement(false)}
      >
        <div className={styles.sensorArrangementWrapper}>
          <AppBar>
            <AppBarTitle>Sensor Arrangement</AppBarTitle>
            <AppBarSection>
              <HorizontalForm>
                <Button
                  leftIcon={<Icons.Collapse />}
                  onClick={() =>
                    setSerialNumberPositions(
                      serialNumberPositions.slice().reverse(),
                    )
                  }
                >
                  Flip Order
                </Button>
              </HorizontalForm>
            </AppBarSection>
          </AppBar>

          {positions.length === 0 ? <p>No sensors in MPI</p> : null}

          <div className={styles.sensorArrangementScroll}>
            {positions.map((_, index) => {
              const value = serialNumberPositions[index];
              return (
                <div className={styles.sensorArrangementRow} key={index}>
                  <span className={styles.sensorArrangementRowTitle}>
                    <Icons.DeviceSide color={colorVariables.gray300} />
                    <span className={styles.sensorArrangementRowTitleText}>
                      Sensor {index + 1}
                    </span>
                  </span>
                  <InputBox
                    type="select"
                    // Each serial number can only be in one slot at a time.
                    invalid={
                      serialNumberPositions.filter(i => i === value).length > 1
                    }
                    choices={positions.map(s => ({
                      id: s.serialNumber,
                      label: s.serialNumber,
                    }))}
                    value={value}
                    onChange={choice => {
                      const newSerialNumberPositions = serialNumberPositions.slice();
                      newSerialNumberPositions[index] = choice.id;
                      setSerialNumberPositions(newSerialNumberPositions);
                    }}
                  />
                </div>
              );
            })}
          </div>
          <AppBarContext.Provider value="BOTTOM_ACTIONS">
            <AppBar>
              <AppBarSection />
              <AppBarSection>
                <HorizontalForm>
                  <Button
                    variant="underline"
                    onClick={() => {
                      setShowSensorArrangement(false);
                      setSerialNumberPositions(
                        positions.map(s => s.serialNumber),
                      );
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    variant="filled"
                    // Ensure that there are no places where the same serial number was entered twice
                    disabled={
                      Array.from(new Set(serialNumberPositions)).length !==
                      serialNumberPositions.length
                    }
                    onClick={() => {
                      onReorderSensors(serialNumberPositions);
                      setShowSensorArrangement(false);
                    }}
                  >
                    Apply
                  </Button>
                </HorizontalForm>
              </AppBarSection>
            </AppBar>
          </AppBarContext.Provider>
        </div>
      </Modal>

      <div className={styles.wrapper}>
        <div className={styles.header}>
          <AppBarContext.Provider value="CARD_HEADER">
            <AppBar>
              <AppBarTitle>Sensors</AppBarTitle>
              <AppBarSection>
                <HorizontalForm>
                  <Button onClick={() => setShowSensorArrangement(true)}>
                    Reorder...
                  </Button>
                  <Button
                    onClick={() => onAddSensor()}
                    variant="filled"
                    leftIcon={<Icons.Plus />}
                  >
                    Add Sensor
                  </Button>
                </HorizontalForm>
              </AppBarSection>
            </AppBar>
          </AppBarContext.Provider>
        </div>

        <div
          className={classnames(styles.cardList, {
            [styles.centered]: renderWithCenteredLayout,
          })}
          data-test-id="mpi-sensor-rearranger"
        >
          {positions.length > 0 ? (
            <div
              className={classnames(styles.sensorCardList, {
                [styles.centered]: renderWithCenteredLayout,
              })}
            >
              {positions.map((left, index) => {
                let betweenElement;
                if (index < positions.length - 1) {
                  const right = positions[index + 1];

                  const selected =
                    focusedUnits[0] === left.serialNumber &&
                    focusedUnits[1] === right.serialNumber;

                  betweenElement = (
                    <div
                      className={classnames(styles.sensorDistance, {
                        [styles.selected]: selected,
                        [styles.inactive]: areUnitsSelected && !selected,
                      })}
                    >
                      <div className={styles.sensorDistanceLine} />
                      <label
                        htmlFor={`mpi-reorder-${index}`}
                        className={styles.sensorDistanceLabel}
                      >
                        Distance (in)
                      </label>
                      <div className={styles.sensorDistanceInputBox}>
                        <InputBox
                          type="text"
                          placeholder='ex: 45"'
                          value={left.spacingRight}
                          invalid={
                            left.spacingRight.length > 0 &&
                            !isNumeric(left.spacingRight)
                              ? 1
                              : 0
                          }
                          onChange={e => {
                            const value: string = e.target.value;
                            onSetSpacingRight(left.serialNumber, value);
                          }}
                          width={96}
                          id={`mpi-reorder-${index}`}
                          data-test-id={`mpi-sensor-rearranger-distance-${index}`}
                          onFocus={() =>
                            setFocusedUnits([
                              left.serialNumber,
                              right.serialNumber,
                            ])
                          }
                          onBlur={() => setFocusedUnits([null, null])}
                        />
                      </div>
                    </div>
                  );
                } else {
                  // TODO: The designs have an add button on the very right end.
                  betweenElement = (
                    <div
                      className={classnames(
                        styles.sensorDistance,
                        styles.addButton,
                      )}
                    >
                      <div className={styles.sensorDistanceLine} />
                      <button
                        className={styles.sensorAddButton}
                        onClick={() => onAddSensor()}
                        data-test-id="mpi-sensor-rearranger-add-sensor"
                      >
                        <Icons.Plus color="#fff" />
                      </button>
                    </div>
                  );
                }

                return (
                  <Fragment key={left.serialNumber}>
                    <div
                      className={classnames(styles.sensorCard, {
                        [styles.selected]: focusedUnits.includes(
                          left.serialNumber,
                        ),
                        [styles.inactive]:
                          areUnitsSelected &&
                          !focusedUnits.includes(left.serialNumber),
                      })}
                      data-test-id="mpi-sensor-rearranger-sensor-card"
                    >
                      <div className={styles.sensorCardHeader}>
                        <h3>{left.serialNumber}</h3>
                        <Tooltip
                          target={
                            <Button
                              onClick={() => onDeleteSensor(left.serialNumber)}
                              leftIcon={
                                <Icons.Close color={colorVariables.red} />
                              }
                              width={40}
                              height={40}
                              variant="underline"
                              data-test-id="mpi-sensor-rearranger-delete-sensor"
                            />
                          }
                          contents="Remove this sensor from the MPI"
                        />
                      </div>

                      <SensorGlyph />

                      <div className={styles.sensorCardFooter}>
                        <div className={styles.sensorCardFooterIndex}>
                          Sensor {index + 1}
                        </div>
                        <div className={styles.sensorCardFooterDoorwayName}>
                          {left.doorwayName || <Fragment>&mdash;</Fragment>}
                        </div>
                      </div>
                    </div>

                    {betweenElement}
                  </Fragment>
                );
              })}
            </div>
          ) : (
            <div className={classnames(styles.sensorCard, styles.emptyState)}>
              <SensorGlyph />
              <Button
                variant="filled"
                onClick={() => onAddSensor()}
                data-test-id="mpi-sensor-rearranger-empty-state-button"
              >
                Add Sensors
              </Button>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default SensorRearranger;

const SensorGlyph: React.FunctionComponent<{}> = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    width="93"
    height="79"
    viewBox="0 0 93 79"
  >
    <defs>
      <rect id="a" width="240" height="272" x="0" y="0" rx="4"></rect>
      <path
        id="c"
        d="M22.668.284C16.687.29 11.391.418 6.921 1.19c-4.074.705-5.016 1.536-5.146 2.454C1.282 8.991.619 18.338.279 30.86h91.443c-.34-12.522-1.004-21.869-1.497-27.216-.13-.918-1.072-1.75-5.144-2.454-4.472-.772-9.767-.9-15.749-.906L22.668.28v.005z"
      ></path>
      <path
        id="e"
        d="M.148 18.424c0 6.42 3.148 9.512 9.58 9.512h25.545c6.432 0 9.58-3.092 9.58-9.512V.552L.148.542v17.882z"
      ></path>
      <path
        id="g"
        d="M1.746.616a.077.077 0 00-.077.077v.809a1.23 1.23 0 01-.59 1.048c-.238.139-.478.277-.717.415a.079.079 0 00-.029.106l.183.315c.02.038.068.05.104.029l.706-.408a1.213 1.213 0 011.205 0l.704.408c.038.02.084.009.106-.029l.182-.315a.08.08 0 00-.028-.106l-.718-.415a1.22 1.22 0 01-.59-1.048V.693A.077.077 0 002.11.616h-.364z"
      ></path>
      <path
        id="i"
        d="M1.01.487c-.386 0-.871.195-.871.62 0 .383.304.521.786.613.106.02.21.037.312.064.119.032.24.081.24.191 0 .198-.226.271-.414.271a.667.667 0 01-.546-.25.084.084 0 00-.098-.02l-.376.176c.198.314.538.477.997.477.404.002.919-.195.919-.662 0-.383-.315-.516-.796-.612a4.684 4.684 0 01-.304-.068C.74 1.254.625 1.207.625 1.1c0-.184.22-.247.392-.247.194 0 .359.067.472.189a.084.084 0 00.097.016l.346-.172C1.747.63 1.422.487 1.017.487H1.01z"
      ></path>
      <path
        id="k"
        d="M.702.699a.078.078 0 00-.04.068v.492H.276v.361c0 .043.034.077.077.077h.31v.913c0 .7.199.931.9.931h.074v-.385a.077.077 0 00-.075-.076c-.364-.009-.407-.156-.407-.475v-.908h.482v-.361a.077.077 0 00-.077-.077h-.405V.451C1.004.533.852.615.702.699z"
      ></path>
      <path
        id="m"
        d="M2.13.593a.088.088 0 00-.082.048l-.684 1.536L.638.639C.625.611.594.593.558.593h-.53l1.06 2.163A519.6 519.6 0 00.54 3.93h.506a.087.087 0 00.079-.048L2.649.592h-.52z"
      ></path>
      <path
        id="o"
        d="M1.549.492A1.056 1.056 0 00.493 1.568c0 .631.469 1.06 1.081 1.06.381 0 .694-.157.894-.451l-.309-.173a.068.068 0 00-.085.015.646.646 0 01-.5.218c-.348 0-.6-.2-.659-.525h1.626a.07.07 0 00.07-.07v-.075c0-.591-.474-1.075-1.053-1.075h-.01zm.01.386c.319 0 .54.17.606.46H.935a.638.638 0 01.624-.46z"
      ></path>
      <path
        id="q"
        d="M.16.211C.12.211.09.247.09.29v3.25h.842c.864 0 1.55-.682 1.55-1.664C2.482.849 1.808.21.916.21H.16zm.376.456H.87c.678 0 1.161.437 1.161 1.208 0 .735-.506 1.204-1.145 1.204H.536V.667z"
      ></path>
      <path
        id="s"
        d="M.396.229v.334c0 .032.026.058.057.058h.311V.286A.058.058 0 00.707.23H.396z"
      ></path>
      <path
        id="u"
        d="M.396.533v1.985c0 .039.026.07.057.07h.311V.601C.764.564.74.533.707.533H.396z"
      ></path>
      <path
        id="w"
        d="M.678.8V.602C.678.564.639.533.591.533H.135v1.985c0 .039.038.07.087.07h.461v-1.13c0-.32.235-.584.65-.584.396 0 .62.2.62.55v1.094c0 .039.038.07.087.07h.466v-1.2c0-.539-.371-.896-1.04-.896C1.143.492.86.602.678.8z"
      ></path>
      <path
        id="y"
        d="M22.669.541C19.55.545 16.607.58 13.64.814c-2.95.237-5.597.639-7.76 1.211-2.154.57-3.67 1.265-4.557 2.038C.433 4.838.3 5.614.279 6.436v12.35c.008.837.289 1.655.876 2.342.745.83 2.162 1.59 4.785 2.286 4.761 1.264 10.367 1.475 16.729 1.482h46.663c6.334-.007 11.916-.218 16.657-1.482 2.612-.697 4.023-1.457 4.764-2.286.649-.687.96-1.505.968-2.341V6.437c-.044-1.715-.902-3.186-5.684-4.434C81.425.8 75.484.55 69.332.54H22.669zM46 .541h23.332-46.663H46z"
      ></path>
      <path id="A" d="M.5.649C.901.649.901.041.5.041S.099.65.5.65z"></path>
      <path
        id="C"
        d="M21.281.518C14.686.527 9.734.76 5.428 1.89 1.207 2.997.33 4.248.285 5.905L.191 18.074c.024 1.67.891 2.93 5.135 4.05 4.337 1.141 9.32 1.377 15.955 1.386H67.72c6.639-.01 11.623-.246 15.955-1.386 4.248-1.12 5.115-2.38 5.136-4.047l-.095-12.17c-.047-1.659-.924-2.91-5.143-4.017C79.261.758 74.31.526 67.72.518H21.281z"
      ></path>
    </defs>
    <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
      <g transform="translate(-147 -137)">
        <g transform="translate(76 57)">
          <mask id="b" fill="#fff">
            <use xlinkHref="#a"></use>
          </mask>
          <rect
            width="241"
            height="273"
            x="-0.5"
            y="-0.5"
            stroke="#DADADA"
            rx="4"
          ></rect>
          <g mask="url(#b)" opacity="0.25">
            <g transform="translate(71 81)">
              <g fillRule="evenodd" stroke="none" strokeWidth="1">
                <mask id="d" fill="#fff">
                  <use xlinkHref="#c"></use>
                </mask>
                <path
                  fill="#FFF"
                  d="M-2.96019718 34.088375L94.961493 34.088375 94.961493 -2.9508125 -2.96019718 -2.9508125z"
                  mask="url(#d)"
                ></path>
              </g>
              <path
                stroke="#222A2E"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.001"
                d="M92 31c-.338-12.694-.999-22.168-1.49-27.588-.128-.931-1.066-1.774-5.12-2.488C80.943.141 75.673.012 69.72.005L23.28 0v.005C17.33.012 12.06.141 7.61.925c-4.054.713-4.99 1.556-5.12 2.487C1.999 8.832 1.339 18.306 1 31"
              ></path>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(24 49)"
              >
                <mask id="f" fill="#fff">
                  <use xlinkHref="#e"></use>
                </mask>
                <path
                  fill="#FFF"
                  d="M-3.06642857 31.1175455L48.0664286 31.1175455 48.0664286 -2.63963636 -3.06642857 -2.63963636z"
                  mask="url(#f)"
                ></path>
              </g>
              <path
                stroke="#222A2E"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.001"
                d="M69 49.01v18.267C69 73.839 65.832 77 59.357 77H33.642C27.168 77 24 73.84 24 67.277V49"
              ></path>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(35 59)"
              >
                <mask id="h" fill="#fff">
                  <use xlinkHref="#g"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-3.01 6.75866667L6.866 6.75866667 6.866 -2.71733333 -3.01 -2.71733333z"
                  mask="url(#h)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(48 60)"
              >
                <mask id="j" fill="#fff">
                  <use xlinkHref="#i"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-3.29066667 5.6286L5.292 5.6286 5.292 -2.5122 -3.29066667 -2.5122z"
                  mask="url(#j)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(51 59)"
              >
                <mask id="l" fill="#fff">
                  <use xlinkHref="#k"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-3.058 6.87533333L4.96933333 6.87533333 4.96933333 -2.88266667 -3.058 -2.88266667z"
                  mask="url(#l)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(53 60)"
              >
                <mask id="n" fill="#fff">
                  <use xlinkHref="#m"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-3.72225 7.26333333L6.399 7.26333333 6.399 -2.74066667 -3.72225 -2.74066667z"
                  mask="url(#n)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(42 60)"
              >
                <mask id="p" fill="#fff">
                  <use xlinkHref="#o"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-2.5068 5.6286L5.6106 5.6286 5.6106 -2.508 -2.5068 -2.508z"
                  mask="url(#p)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(40 59)"
              >
                <mask id="r" fill="#fff">
                  <use xlinkHref="#q"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-2.91 6.872L5.4816 6.872 5.4816 -3.12133333 -2.91 -3.12133333z"
                  mask="url(#r)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(50 60)"
              >
                <mask id="t" fill="#fff">
                  <use xlinkHref="#s"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-2.104 3.121L3.2645 3.121 3.2645 -2.2715 -2.104 -2.2715z"
                  mask="url(#t)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(50 60)"
              >
                <mask id="v" fill="#fff">
                  <use xlinkHref="#u"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-2.104 5.5878L3.2645 5.5878 3.2645 -2.4666 -2.104 -2.4666z"
                  mask="url(#v)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(45 60)"
              >
                <mask id="x" fill="#fff">
                  <use xlinkHref="#w"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-3.615 5.5878L6.25575 5.5878 6.25575 -2.5074 -3.615 -2.5074z"
                  mask="url(#x)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(0 24)"
              >
                <mask id="z" fill="#fff">
                  <use xlinkHref="#y"></use>
                </mask>
                <path
                  fill="#FFF"
                  d="M-2.96019718 28.1855263L94.9608451 28.1855263 94.9608451 -2.74802632 -2.96019718 -2.74802632z"
                  mask="url(#z)"
                ></path>
              </g>
              <path
                stroke="#222A2E"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.001"
                d="M69.719 25c6.122.008 12.035.255 16.625 1.44 4.759 1.23 5.612 2.68 5.656 4.368V42.98c-.008.825-.318 1.631-.963 2.308-.738.817-2.143 1.566-4.742 2.252C81.578 48.785 76.022 48.993 69.72 49H23.281c-6.33-.007-11.91-.215-16.648-1.46-2.61-.687-4.02-1.436-4.761-2.253A3.537 3.537 0 011 42.98V30.81c.022-.81.153-1.575 1.04-2.339.883-.761 2.39-1.447 4.535-2.009 2.152-.563 4.787-.96 7.722-1.192 2.951-.233 5.88-.265 8.984-.269H69.72 23.28"
              ></path>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(46 12)"
              >
                <mask id="B" fill="#fff">
                  <use xlinkHref="#A"></use>
                </mask>
                <path
                  fill="#353535"
                  d="M-2.301 3.1485L3.3005 3.1485 3.3005 -2.4585 -2.301 -2.4585z"
                  mask="url(#B)"
                ></path>
              </g>
              <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
                transform="translate(2 25)"
              >
                <mask id="D" fill="#fff">
                  <use xlinkHref="#C"></use>
                </mask>
                <path
                  fill="#222A2E"
                  d="M-3.03373913 26.7528649L92.0343841 26.7528649 92.0343841 -2.72497297 -3.03373913 -2.72497297z"
                  mask="url(#D)"
                ></path>
              </g>
            </g>
          </g>
        </g>
      </g>
    </g>
  </svg>
);
